import { normalizeLedgerFields } from "../../lib/ledger-field-compat";
import type { ExpenseInput } from "../../types";
import { prepareExpenseForSync } from "../../lib/offline-associated";
export type BatchItem = {
  expense: ExpenseInput;
  status: "draft" | "pending" | "saved" | "error";
  error?: string;
  transactionId?: string;
  savedPriceIds?: string[];
  validationRejected?: boolean;
};
export const batchKey = (profile: string) => `barnyard-batch-${profile}`;
export function readBatch(profile: string): BatchItem[] | null {
  try {
    const value = JSON.parse(localStorage.getItem(batchKey(profile)) || "null");
    if (
      Array.isArray(value) &&
      value.length > 0 &&
      value.length <= 20 &&
      value.every(
        (i) =>
          i.expense &&
          typeof i.expense.requestId === "string" &&
          ["draft", "pending", "saved", "error"].includes(i.status),
      )
    )
      return normalizeLedgerFields(value);
  } catch {
    /* A missing journal does not start a write. */
  }
  return null;
}
export function editBatchItem(
  item: BatchItem,
  expense: ExpenseInput,
): BatchItem {
  if (item.status === "pending" || item.status === "saved")
    throw new Error(
      "This transaction is locked while its save is being confirmed.",
    );
  return {
    expense: { ...expense, requestId: crypto.randomUUID() },
    status: "draft",
  };
}
export async function runBatch(
  items: BatchItem[],
  persist: (items: BatchItem[]) => void,
  request: typeof fetch = fetch,
  active: () => boolean = () => true,
) {
  let next = items.map((i) => ({ ...i, expense: { ...i.expense } }));
  if (
    !next.length ||
    next.length > 20 ||
    new Set(next.map((i) => i.expense.requestId)).size !== next.length
  )
    throw new Error("Invalid batch references. Reopen the draft.");
  // Must succeed before any network request. Pending items retain their original IDs.
  persist(next);
  for (let index = 0; index < next.length; index++) {
    if (!active()) break;
    const item = next[index];
    if (item.status === "saved") continue;
    const wasPending = item.status === "pending";
    next = next.map((v, i) =>
      i === index ? { ...v, status: "pending", error: undefined, validationRejected: undefined } : v,
    );
    persist(next);
    let updated: BatchItem;
    try {
      const response = item.transactionId
        ? Response.json({ verified: true, id: item.transactionId })
        : await request("/api/expenses", {
            method: "POST",
            credentials: "same-origin",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...prepareExpenseForSync(item.expense), trackedItems: undefined }),
            signal: AbortSignal.timeout(45000),
          });
      const body = (await response.json()) as {
        verified?: boolean;
        uncertain?: boolean;
        error?: string;
        id?: string;
        validationRejected?: boolean;
      };
      if (response.ok && body.verified && body.id) {
        item.transactionId = body.id;
        item.savedPriceIds = [...(item.savedPriceIds || [])];
        next[index] = { ...item, status: "pending" };
        persist(next);
        for (const tracked of item.expense.trackedItems || []) {
          if (item.savedPriceIds.includes(tracked.id)) continue;
          if (!active())
            throw new Error(
              "Expense saved. Some tracked items still need saving.",
            );
          const priceResponse = await request("/api/prices/add", {
            method: "POST",
            credentials: "same-origin",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ transactionId: body.id, ...tracked }),
            signal: AbortSignal.timeout(45000),
          });
          const priceBody = (await priceResponse.json()) as {
            error?: string;
            verified?: boolean;
          };
          if (!priceResponse.ok || !priceBody.verified)
            throw new Error(
              priceBody.error || "Price save needs verification.",
            );
          item.savedPriceIds.push(tracked.id);
          next[index] = {
            ...item,
            status: "pending",
            savedPriceIds: [...item.savedPriceIds],
          };
          persist(next);
        }
        updated = { ...item, status: "saved", error: undefined };
      } else {
        const definitive =
          body.uncertain === false ||
          (!wasPending && [400, 401, 403, 413].includes(response.status));
        updated = {
          ...item,
          status: definitive ? "error" : "pending",
          validationRejected: response.status === 400 && body.uncertain === false && body.validationRejected === true && !item.transactionId,
          error: body.error || "Save not confirmed. Check remaining saves.",
        };
      }
    } catch (error) {
      updated = {
        ...item,
        status: "pending",
        validationRejected: undefined,
        error: item.transactionId
          ? `Expense saved · ${item.savedPriceIds?.length || 0} of ${item.expense.trackedItems?.length || 0} prices saved. Retry remaining. ${error instanceof Error ? error.message : ""}`
          : "Connection interrupted. Check remaining saves; this entry will not be added twice.",
      };
    }
    next = next.map((v, i) => (i === index ? updated : v));
    persist(next);
    // A partial batch is explicit: leave later items unsent until the user retries.
    if (updated.status !== "saved") break;
  }
  return next;
}
